// applePlatform — iOS / iPadOS (= タッチ可能な Apple WebKit デバイス) 判定の
// pure helper (consult 2026-06-02).
//
// ## 背景・経緯
//
// iPad / iPadOS は Safari の仕様で「画面を下にスワイプすると Fullscreen API の
// 全画面が解除される」. これは OS 挙動で Web 側から抑止できない. PWA standalone も
// 動線不整合で不採用 (consult 2026-06-02). そこで「全画面ボタンを押した人にだけ、
// スワイプ解除の注意を 1 回だけ出す」据え置き対応にする. その「出すべき対象」を
// 判定するのがこの helper.
//
// ## 判定の罠 (要対応)
//
// - **ブラウザブランドで判定しない**: iPad の Chrome / Firefox / Edge も中身は
//   WebKit で同じ挙動なので、ブラウザ名ではなく「タッチ可能な Apple プラット
//   フォームか」で判定する.
// - **iPadOS Safari はデスクトップ Mac の UA を返す** (= "desktop class" UA、
//   `Macintosh` を含む). そのため UA 文字列 (`/iPad/`) だけでは iPad を取りこぼす.
//   `Macintosh` を含み、かつ `maxTouchPoints > 1` (= 物理タッチ可能) を併用して
//   「Mac を騙る iPad」を拾う. デスクトップの本物の Mac は `maxTouchPoints === 0`
//   なので除外される.
// - **iPhone / iPod** は UA に `iPhone` / `iPod` を含むので `/iP(hone|od)/` で拾える
//   (= 旧来どおり実機 UA を返す).
//
// ## なぜ pure helper として外出ししたか
//
// `packages/web` の vitest は **node env, no DOM** (`packages/web/vitest.config.ts:9`).
// `navigator` を直接読むと node 環境で組み立てられないため、判定を pure 関数に
// 切り出し、必要な値 (userAgent / maxTouchPoints / platform) を引数で受ける形にして
// 単体テスト可能にする (= 既存 `penOnlyMode.ts` / `argbModeToggle.ts` と同流儀).
// 呼び出し側 (ClassicLayout) は `navigator` から値を読んで渡すだけ.

/**
 * `isTouchAppleDevice` に渡す navigator 風の最小情報.
 *
 * - `userAgent`: `navigator.userAgent`.
 * - `maxTouchPoints`: `navigator.maxTouchPoints` (= 物理タッチ点数. 本物の
 *   デスクトップ Mac は 0、iPad / iPhone は 1 以上).
 * - `platform`: `navigator.platform` (= 任意. iPadOS では `MacIntel` を返すことが
 *   あるため、UA の `Macintosh` 判定の補助に使う. 省略時は UA のみで判定).
 */
export interface NavigatorLike {
  readonly userAgent: string;
  readonly maxTouchPoints: number;
  readonly platform?: string;
}

/**
 * 「タッチ可能な iOS / iPadOS デバイス (= WebKit による全画面スワイプ解除挙動を
 * 持つ環境)」かどうかの判定.
 *
 * true になるのは:
 *   1. iPhone / iPod: UA に `iPhone` / `iPod` を含む.
 *   2. iPad (実機 UA): UA に `iPad` を含む.
 *   3. iPadOS Safari (Mac 偽装 UA): UA / platform が Apple desktop (`Macintosh` /
 *      `MacIntel`) を名乗り、かつ `maxTouchPoints > 1` (= 物理タッチ可能).
 *
 * false になるのは:
 *   - 本物のデスクトップ Mac (= `Macintosh` だが `maxTouchPoints === 0`).
 *   - Windows / Android / その他 (= Apple プラットフォームを名乗らない).
 *
 * ブラウザブランド (Safari / Chrome 等) は一切見ない (= iPad の Chrome / Firefox /
 * Edge も中身は WebKit で同挙動のため).
 */
export const isTouchAppleDevice = (nav: NavigatorLike): boolean => {
  const ua = nav.userAgent;
  // iPhone / iPod は実機 UA をそのまま返す (= touch 点数を問わず true).
  if (/iP(hone|od)/.test(ua)) return true;
  // iPad の実機 UA (= 古い iPadOS / 一部設定で `iPad` を含む場合).
  if (/iPad/.test(ua)) return true;
  // iPadOS Safari は Mac の UA / platform を騙る. `Macintosh` (UA) または
  // `MacIntel` 等 (platform) を名乗り、かつ物理タッチ可能 (maxTouchPoints > 1)
  // なら iPad とみなす. 本物の Mac は maxTouchPoints === 0 なので除外される.
  const looksLikeAppleDesktop = /Macintosh/.test(ua) || /Mac/.test(nav.platform ?? '');
  if (looksLikeAppleDesktop && nav.maxTouchPoints > 1) return true;
  return false;
};
